"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import type { Media, Post } from "@/payload-types";
import { getMediaUrl, type MediaSize } from "@/utilities/media/getMediaUrl";
import { cn } from "@/utilities/core/cn";
import { getEmbedUrl } from "../../utils/getEmbedUrl";

interface VideoEmbedProps {
    videoEmbed?: Post["videoEmbed"] | null;
    heroImage?: Media | null;
    title: string;
    /** Extra classes applied to the aspect-video wrapper. */
    className?: string;
    /** Media size preset used when falling back to the hero image. */
    heroSize?: MediaSize;
    /** <Image sizes> attribute for the hero fallback. */
    imageSizes?: string;
    /** Whether to mark the hero image as high-priority for LCP. */
    priority?: boolean;
}

/**
 * Renders a video post's playable surface, in priority order:
 * 1. iframe embed URL (YouTube, Vimeo, Dailymotion, Facebook)
 * 2. Raw embedCode HTML (script tags re-executed on mount so TikTok / Instagram / Facebook work)
 * 3. Hero image fallback
 * Returns null when none of the above are available.
 */
export function VideoEmbed({
    videoEmbed,
    heroImage,
    title,
    className,
    heroSize = "xlarge",
    imageSizes = "(max-width: 768px) 100vw, 960px",
    priority = false,
}: VideoEmbedProps) {
    const embedCodeRef = useRef<HTMLDivElement>(null);
    const embedUrl = videoEmbed ? getEmbedUrl(videoEmbed) : null;
    const embedCode = videoEmbed?.embedCode || null;

    // Scripts injected via dangerouslySetInnerHTML don't execute — recreate them so
    // platforms like TikTok / Instagram / Facebook that ship <script> tags bootstrap.
    useEffect(() => {
        const el = embedCodeRef.current;
        if (!el || embedUrl || !embedCode) return;
        const scripts = Array.from(el.querySelectorAll("script"));
        scripts.forEach((oldScript) => {
            const newScript = document.createElement("script");
            for (const attr of Array.from(oldScript.attributes)) {
                newScript.setAttribute(attr.name, attr.value);
            }
            newScript.text = oldScript.text;
            oldScript.parentNode?.replaceChild(newScript, oldScript);
        });
    }, [embedCode, embedUrl]);

    const wrapperClass = cn("relative aspect-video rounded-lg overflow-hidden", className);

    if (embedUrl) {
        return (
            <div className={wrapperClass}>
                <iframe
                    src={embedUrl}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={title}
                />
            </div>
        );
    }

    if (embedCode) {
        return (
            <div
                ref={embedCodeRef}
                className={cn(
                    wrapperClass,
                    "[&>iframe]:absolute [&>iframe]:inset-0 [&>iframe]:w-full [&>iframe]:h-full",
                )}
                dangerouslySetInnerHTML={{ __html: embedCode }}
            />
        );
    }

    const heroImageUrl = heroImage ? getMediaUrl(heroImage, heroSize) || heroImage.url : null;
    if (heroImage && heroImageUrl) {
        return (
            <div className={wrapperClass}>
                <Image
                    src={heroImageUrl}
                    alt={heroImage.alt || title}
                    fill
                    sizes={imageSizes}
                    className="object-cover"
                    priority={priority}
                    unoptimized
                />
            </div>
        );
    }

    return null;
}
